import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPage = () => (
  <section className="bg-cream-100 py-16">
    <div className="max-w-3xl mx-auto px-5 lg:px-10">
      <Link to="/" className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
      <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Legal</p>
      <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">Privacy <span className="italic">Policy</span></h1>
      <p className="text-sm text-navy-700/60 mt-2">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="max-w-none mt-10 space-y-8 text-navy-700/85 leading-relaxed">
        <p className="text-lg">Your privacy matters. This Privacy Policy describes what data galeriecop collects, why, and how we protect it.</p>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">1. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Account info</strong> — your name, email and a securely hashed password.</li>
            <li><strong>Event info</strong> — the name, date, type and description you provide for your event.</li>
            <li><strong>Uploaded content</strong> — photos, videos and guestbook messages uploaded by you or your guests.</li>
            <li><strong>Guest info</strong> — the display name a guest types when uploading. We do not require guests to register.</li>
            <li><strong>Payment info</strong> — handled entirely by Stripe. We only receive the transaction reference and tier purchased.</li>
            <li><strong>Usage data</strong> — basic logs (IP, browser, page views) to keep the Service running and secure.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To operate, maintain and improve the Service.</li>
            <li>To allow guests to upload to your private gallery via your QR code or link.</li>
            <li>To send transactional emails (account, payment, expiry reminders).</li>
            <li>To detect, prevent and address abuse, fraud or security issues.</li>
          </ul>
          <p className="mt-3">We do <strong>not</strong> sell your personal data or your guests’ uploaded content to anyone.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">3. Content Ownership</h2>
          <p>100% of the copyright of uploaded photos, videos and messages stays with you and your guests. We only host this content to operate your gallery.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">4. Data Storage &amp; Security</h2>
          <p>Data is stored on encrypted infrastructure. Passwords are hashed with bcrypt. Payments are tokenized via Stripe. Access to production systems is restricted to authorized engineers.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">5. Third-Party Services (Data Processors)</h2>
          <p className="mb-2">We use the following processors to operate galeriecop. Each has their own privacy policy and appropriate GDPR safeguards:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Stripe</strong> — payment processing (PCI DSS compliant, stores card data on your behalf; we never see the card number)</li>
            <li><strong>Cloudflare R2</strong> — photo &amp; video storage (encrypted at rest, EU-region option available)</li>
            <li><strong>Resend</strong> — transactional email delivery</li>
            <li><strong>Emergent LLM</strong> — powers the AI Highlights Reel and Help chat (only event metadata + prompts are sent; no personally identifying content leaves our servers)</li>
            <li><strong>Frankfurter.app</strong> — daily FX rates for multi-currency pricing (no personal data sent)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">6. Legal Basis for Processing (GDPR / UK GDPR)</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Contract</strong> — to deliver the paid service you signed up for (account, events, uploads, payments).</li>
            <li><strong>Legitimate interest</strong> — service abuse prevention, security, and product improvements.</li>
            <li><strong>Consent</strong> — non-essential cookies (analytics), guest uploads (uploader consent captured on the upload page).</li>
            <li><strong>Legal obligation</strong> — tax/accounting records for paid transactions (retained 6 years).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">7. Cookies</h2>
          <p>We use essential cookies for authentication and Service operation, and optional cookies for analytics. You can accept, reject, or customise your choices from the cookie banner shown on your first visit — or by clicking &quot;Cookie settings&quot; in the footer at any time.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">8. Your Rights &amp; How to Exercise Them</h2>
          <p className="mb-2">Under GDPR / UK GDPR you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Access</strong> your data — self-service via the &quot;Download my data&quot; button on your <Link to="/dashboard" className="text-coral-400 hover:text-coral-500">dashboard</Link>.</li>
            <li><strong>Erasure</strong> — self-service via the &quot;Delete my account&quot; button on your dashboard (cascades across events, uploads, payments; irreversible).</li>
            <li><strong>Rectification</strong> — email <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a> and we&apos;ll correct any inaccurate data within 30 days.</li>
            <li><strong>Portability</strong> — the &quot;Download my data&quot; export is a machine-readable JSON; you can import it elsewhere or ask us to transmit it.</li>
            <li><strong>Object / restrict processing</strong> — email us to withdraw consent for non-essential processing.</li>
            <li><strong>Complain</strong> — you may lodge a complaint with your national supervisory authority (in the UK: the Information Commissioner&apos;s Office, ico.org.uk).</li>
          </ul>
          <p className="mt-3">Guest photo take-downs: if you appear in a photo on someone&apos;s gallery and want it removed, email the event host directly (their email is visible on the event page) or contact us at <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a> and we will forward your request.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">9. Data Retention</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Account &amp; event data — retained while your account is active.</li>
            <li>Uploaded photos &amp; videos — retained while the event is active. After the event tier expires and is not renewed, media is deleted 90 days after expiry.</li>
            <li>Payment records — retained 6 years to satisfy accounting/tax obligations.</li>
            <li>Consent audit log — retained 6 years for regulatory compliance.</li>
            <li>Backups — residual copies are purged within 30 days of deletion.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">10. Children</h2>
          <p>The Service is not directed at children under 16. We do not knowingly collect personal information from children under 16 without verifiable parental consent.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">11. Changes</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be communicated via email or the Service.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">12. Contact / Data Controller</h2>
          <p>galeriecop is the data controller for the personal data we process. Questions or requests about your privacy? Email <a href="mailto:privacy@galeriecop.com" className="text-coral-400 hover:text-coral-500">privacy@galeriecop.com</a>.</p>
        </section>
      </div>
    </div>
  </section>
);

export default PrivacyPage;
